import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const LearningContext = createContext();

export const useLearning = () => {
    const context = useContext(LearningContext);
    if (!context) {
        throw new Error('useLearning must be used within a LearningProvider');
    }
    return context;
};

export const LearningProvider = ({ children }) => {
    const [settings, setSettings] = useState({
        mode: 'show_on_x_times', // 'always', 'show_on_x_times', 'never'
        showCount: 3
    });

    const [tooltipViews, setTooltipViews] = useState({});

    // Load settings and views from localStorage on mount
    useEffect(() => {
        const savedSettings = localStorage.getItem('learning-tooltip-settings');
        const savedViews = localStorage.getItem('learning-tooltip-views');
        
        if (savedSettings) {
            setSettings(JSON.parse(savedSettings));
        }
        
        if (savedViews) {
            setTooltipViews(JSON.parse(savedViews));
        }
    }, []);

    // Save settings to localStorage when they change
    useEffect(() => {
        localStorage.setItem('learning-tooltip-settings', JSON.stringify(settings));
    }, [settings]);

    // Save tooltip views to localStorage when they change
    useEffect(() => {
        localStorage.setItem('learning-tooltip-views', JSON.stringify(tooltipViews));
    }, [tooltipViews]);

    const shouldShowTooltip = useCallback((tooltipId) => {
        console.log('viewCount', settings.mode, tooltipViews[tooltipId] || 0, settings.showCount);

        if (settings.mode === 'never') {
            return false;
        }
        
        if (settings.mode === 'always') {
            return true;
        }
        
        if (settings.mode === 'show_on_x_times') {
            const viewCount = tooltipViews[tooltipId] || 0;
            return viewCount < settings.showCount;
        }
        
        return false;
    }, [settings.mode, settings.showCount, tooltipViews]);

    const markTooltipAsSeen = useCallback((tooltipId) => {
        setTooltipViews(prev => ({
            ...prev,
            [tooltipId]: (prev[tooltipId] || 0) + 1
        }));
    }, []);

    const updateSettings = (newSettings) => {
        setSettings(prev => ({ ...prev, ...newSettings }));
    };

    const resetTooltipViews = (tooltipId = null) => {
        if (tooltipId) {
            setTooltipViews(prev => {
                const newViews = { ...prev };
                delete newViews[tooltipId];
                return newViews;
            });
        } else {
            setTooltipViews({});
        }
    };

    const resetAllSettings = () => {
        setSettings({
            mode: 'always',
            showCount: 3
        });
        setTooltipViews({});
        localStorage.removeItem('learning-tooltip-settings');
        localStorage.removeItem('learning-tooltip-views');
    };

    const value = {
        settings,
        tooltipViews,
        shouldShowTooltip,
        markTooltipAsSeen,
        updateSettings,
        resetTooltipViews,
        resetAllSettings
    };

    return (
        <LearningContext.Provider value={value}>
            {children}
        </LearningContext.Provider>
    );
};
